<script setup lang="ts">
import enUS from 'ant-design-vue/es/locale/en_US';
import zhCN from 'ant-design-vue/es/locale/zh_CN';
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/zh-cn';
import { EnumLocale } from '@ais/common-shared';
import { EnumPath, EnumPermissions } from '@ais-web/commons/shared/enum';
import { useRouterPush } from '@ais-web/commons/shared/hooks';
import { getLocale, getToken } from '@ais-web/commons/shared/utils';
import { theme } from '@ais-web/commons/shared/utils/theme';

/** 判断是否已登录 */
const isLogin = !!getToken();

const computedLanguage = computed(() => {
  const lang = getLocale();

  switch (lang) {
    case EnumLocale.EnUs: {
      dayjs.locale('en');

      return {
        antdLanguage: enUS,
      };
    }

    default: {
      dayjs.locale('zh-cn');

      return {
        antdLanguage: zhCN,
      };
    }
  }
});

const route = useRoute();
const router = useRouter();
const routerPush = useRouterPush();

/** 默认的 meta 配置 */
const DEFAULT_META: Meta = { header: true, menu: true, minHeight: true, minWidth: true };

/** meta 信息 */
const meta = ref<Meta>({ ...DEFAULT_META, ...route.meta } as Meta);

router.beforeEach((to) => {
  meta.value = { ...DEFAULT_META, ...to.meta };
});

/**
 * @description 自定义处理点击面包屑
 * @author gefeiyang
 */
const handleClickBreadcrumbCustom = (targetPath: EnumPath) => {
  // 客户管理详情需要带上 tenantId 参数
  if (targetPath === EnumPath.CustomerMgmtCustomerDetail) {
    const tenantId = route.query.tenantId;
    if (typeof tenantId !== 'string' || !tenantId) {
      return;
    }
    routerPush(targetPath, { tenantId });
    return;
  }
  routerPush(targetPath);
};
</script>

<template>
  <a-config-provider :locale="computedLanguage.antdLanguage" :theme="theme">
    <a-app>
      <base-browser-check-tip />
      <a-layout
        :class="['app-layout', { 'min-height': meta.minHeight, 'min-width': meta.minWidth }]"
      >
        <a-layout class="main-layout">
          <the-menu v-if="meta.menu" />
          <a-layout-content>
            <a-layout-header v-if="meta.header">
              <the-header :handle-click-breadcrumb-custom="handleClickBreadcrumbCustom" />
            </a-layout-header>
            <router-view />
          </a-layout-content>
        </a-layout>
      </a-layout>
      <base-auth :ids="[EnumPermissions.MessageCenter]">
        <the-message v-if="isLogin" />
      </base-auth>
      <!-- 全局回到顶部/底部按钮，在需要的滚动容器上添加 id="base-back-to-container" 即可启用 -->
      <base-back-to />
    </a-app>
  </a-config-provider>
</template>

<style lang="less" scoped>
.ant-app {
  height: 100%;

  .app-layout {
    height: 100%;

    &.min-height {
      min-height: 100%;
    }

    &.min-width {
      min-width: 1280px;
    }

    .ant-layout-header {
      padding: 14px 0;
      display: flex;
      align-items: center;
      height: 48px;
      line-height: 1;
      border-bottom: 1px solid var(--ant-grey-11);
      background-color: var(--ant-grey-13);
    }

    .main-layout {
      flex: 1;
      background-color: var(--ant-grey-12);

      .ant-layout-content {
        height: 100%;
        overflow: auto;
        display: flex;
        flex-direction: column;
      }
    }
  }
}
</style>
